import React, { useState, useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { Typography, Button, colors, Badge } from '@sweep-io/sweep-design';
import { ArrowRight } from '@sweep-io/sweep-design/dist/icons';
import { InfoDialog } from '../../common/dialogs/InfoDialog';
import { DocumentationContextProvider } from '../../documentation/context/DocumentationContext';
import { DocumentationFiltersProvider } from '../../documentation/selected-object/filters/DocumentationFiltersContext';
import useCreateAgent from '../useCreateAgent';
import { AgentSelectionType } from '../../../types/enums/AgentType';
import { AgentType, MonitoringV2ContextComponent } from '@server/ai';
import { ObjectTabsForAgent } from './ObjectTabsForAgent';
import { MetadataTableForAgent } from './MetadataTableForAgent';
import { getObjectNameColor } from '../../documentation/helpers';
import { telemetry } from '../../../telemetry';
import pluralize from 'pluralize';
import BestPracticesContent from '../../bestPractices/BestPracticesContent';
import { AiAgent } from '../aiAgentsConsts';
import useConfirm from '../../common/dialogs/ConfirmLeaveWithoutSave/useConfirm';
import { ConfirmDialog } from '../../common/dialogs/ConfirmDialog';

interface AgentContextSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  agentType: AgentSelectionType;
  objectType?: string;
  metadataType?: string;
  crmOrgId: string;
}

export interface SelectionItem {
  name: string;
  type: string;
}

type ModalStep = 'table-selection' | 'best-practices';

const createMockAgentForFiltering = (
  agentType: AgentSelectionType,
  objectType?: string,
  metadataType?: string,
  crmOrgId?: string,
): AiAgent | undefined => {
  if (!crmOrgId) return undefined;

  const baseAgent: Partial<AiAgent> = {
    id: '',
    name: '',
    type: AgentType.MonitoringV2,
    updatedAt: '',
    files: [],
    orgId: crmOrgId,
    bestPractices: [],
  };

  if (agentType === AgentSelectionType.METADATA_TYPES && metadataType) {
    return {
      ...baseAgent,
      contextComponents: { metadataType },
    } as AiAgent;
  }

  if (agentType === AgentSelectionType.OBJECT_TYPES && objectType) {
    return {
      ...baseAgent,
      contextComponents: { objectType },
    } as AiAgent;
  }

  return undefined;
};

export const AgentContextSelectionModal: React.FC<AgentContextSelectionModalProps> = ({
  isOpen,
  onClose,
  agentType,
  objectType,
  metadataType,
  crmOrgId,
}) => {
  const [currentStep, setCurrentStep] = useState<ModalStep>('table-selection');
  const [selections, setSelections] = useState<SelectionItem[]>([]);
  const [selectedBestPracticeIds, setSelectedBestPracticeIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const createAgent = useCreateAgent();
  const { openConfirm, onConfirm, onCancel } = useConfirm();

  const totalSelected = selections.length;

  const modalTitle = useMemo(() => {
    const isMetadataType = agentType === AgentSelectionType.METADATA_TYPES;
    const displayText = isMetadataType ? metadataType : objectType;
    const backgroundColor = isMetadataType
      ? colors.grey[200]
      : getObjectNameColor(objectType || '');

    return (
      <Typography variant="h3">
        Set up monitoring agent for{' '}
        <span
          style={{
            backgroundColor,
            padding: '4px 8px',
            borderRadius: '2px',
          }}
        >
          {displayText}
        </span>
      </Typography>
    );
  }, [agentType, objectType, metadataType]);

  const handleSelectionChange = (newSelections: SelectionItem[]) => {
    if (isSubmitting) return;
    setSelections(newSelections);
  };

  const handleBestPracticesChange = (bestPracticeIds: string[]) => {
    if (isSubmitting) return;
    setSelectedBestPracticeIds(bestPracticeIds);
  };

  const handleNext = () => {
    if (currentStep === 'table-selection') {
      setCurrentStep('best-practices');
    }
  };

  const handleBack = () => {
    if (currentStep === 'best-practices') {
      setCurrentStep('table-selection');
    }
  };

  const handleCreateAgent = async () => {
    if (selectedBestPracticeIds.length === 0) {
      const confirmed = await openConfirm(
        <ConfirmDialog
          isOpen={true}
          title="Setup incomplete"
          onConfirm={() => {
            onConfirm();
          }}
          onCancel={() => {
            onCancel();
            setCurrentStep('best-practices');
          }}
          confirmText="Create anyway"
          cancelText="Add now"
          confirmButtonType="primary"
        >
          <Typography variant="body">
            Best practices are required for the agent to find issues.
          </Typography>
        </ConfirmDialog>,
      );

      if (!confirmed) return;
    }

    const contextComponents: MonitoringV2ContextComponent = {
      objectType: agentType === AgentSelectionType.OBJECT_TYPES ? objectType : undefined,
      metadataType: agentType === AgentSelectionType.METADATA_TYPES ? metadataType : undefined,
      includeConfigurations: selections,
    };

    setIsSubmitting(true);

    try {
      await createAgent({
        type: AgentType.MonitoringV2,
        name: `${agentType === AgentSelectionType.OBJECT_TYPES ? objectType : metadataType} Monitoring`,
        selectedContext: contextComponents,
        bestPracticeIds: selectedBestPracticeIds,
      });
      onClose();
    } catch (error) {
      telemetry.captureError(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isObjectType = agentType === AgentSelectionType.OBJECT_TYPES;

  return (
    <InfoDialog
      showCloseButton={!isSubmitting}
      titleTypographyVariant="h2"
      open={isOpen}
      transitionDuration={0}
      handleClose={isSubmitting ? () => {} : onClose}
      titleJsx={modalTitle}
      PaperPropsSx={{ width: '1447px', height: '918px' }}
      dialogActions={
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
          }}
        >
          {currentStep === 'table-selection' ? (
            <>
              <Typography variant="body">
                {totalSelected > 0 ? <strong>{totalSelected}</strong> : 'No'}{' '}
                {pluralize('item', totalSelected)} selected
              </Typography>

              <Button onClick={handleNext} disabled={totalSelected === 0 || isSubmitting}>
                Next
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body">
                {selectedBestPracticeIds.length > 0 ? (
                  <Typography variant="body-bold">{selectedBestPracticeIds.length}</Typography>
                ) : (
                  'No'
                )}{' '}
                best {pluralize('practice', selectedBestPracticeIds.length)} selected
              </Typography>

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="secondary" onClick={handleBack} disabled={isSubmitting}>
                  Back
                </Button>
                <Button onClick={handleCreateAgent} disabled={isSubmitting} loading={isSubmitting}>
                  Create Agent
                </Button>
              </Box>
            </>
          )}
        </Box>
      }
    >
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          paddingTop: '8px',
          position: 'relative',
          ...(isSubmitting && {
            pointerEvents: 'none',
            opacity: 0.6,
          }),
        }}
      >
        <Stack direction="row" alignItems="center" gap="16px" sx={{ marginBottom: '16px' }}>
          <Stack direction="row" alignItems="center" gap="8px">
            <Badge
              variant={currentStep === 'table-selection' ? 'primary' : 'secondary'}
              label={1}
              showZero
            />
            <Typography
              variant="body"
              color={currentStep === 'table-selection' ? colors.blue[500] : colors.grey[700]}
            >
              Configurations
            </Typography>
          </Stack>

          <ArrowRight size="medium" />

          <Stack direction="row" alignItems="center" gap="8px">
            <Badge
              variant={currentStep === 'best-practices' ? 'primary' : 'secondary'}
              label={2}
              showZero
            />
            <Typography
              variant="body"
              color={currentStep === 'best-practices' ? colors.blue[500] : colors.grey[700]}
            >
              Best Practices
            </Typography>
          </Stack>
        </Stack>

        {currentStep === 'table-selection' ? (
          <DocumentationContextProvider crmOrgId={crmOrgId}>
            <DocumentationFiltersProvider>
              {isObjectType ? (
                <ObjectTabsForAgent
                  objectType={objectType!}
                  crmOrgId={crmOrgId}
                  onSelectionChange={handleSelectionChange}
                  isSubmitting={isSubmitting}
                />
              ) : (
                <MetadataTableForAgent
                  metadataType={metadataType!}
                  crmOrgId={crmOrgId}
                  onSelectionChange={handleSelectionChange}
                  isSubmitting={isSubmitting}
                />
              )}
            </DocumentationFiltersProvider>
          </DocumentationContextProvider>
        ) : (
          <BestPracticesContent
            agentType={AgentType.MonitoringV2}
            onBestPracticesChange={handleBestPracticesChange}
            selectedBestPracticeIds={selectedBestPracticeIds}
            isAgentCreationContext={true}
            agent={createMockAgentForFiltering(agentType, objectType, metadataType, crmOrgId)}
          />
        )}
      </Box>
    </InfoDialog>
  );
};
